import { ReactElement, useState } from "react";
import { Typography, TextField, Box, Divider, Button } from "@mui/material";

export interface DimensionalityReductionFormProps {
  onSubmit?: (data: DimensionalityReductionData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface DimensionalityReductionData {
  n_comps: number;
}

const DimensionalityReductionForm = ({
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: DimensionalityReductionFormProps): ReactElement => {
  const [formData, setFormData] = useState<DimensionalityReductionData>({
    n_comps: 50,
  });

  const handleInputChange = (
    field: keyof DimensionalityReductionData,
    value: number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <Box>
        <Typography
          variant="body2"
          className="text-gray-600 mb-4 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Dimensionality reduction reduces the number of features (genes) while
          preserving the most important information. Principal Component
          Analysis (PCA) is commonly used to identify the main sources of
          variation in your single-cell data.
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          className="mb-6 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Configure the number of principal components to compute:
        </Typography>
      </Box>

      <Divider className="mb-6" />

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        <TextField
          fullWidth
          label="Number of Components"
          type="number"
          value={formData.n_comps}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("n_comps", 0);
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue) && numValue >= 1) {
                handleInputChange("n_comps", numValue);
              }
            }
          }}
          inputProps={{
            min: 1,
            step: 1,
          }}
          helperText="Number of principal components to compute (default: 50)"
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between mt-6">
        {onBack && (
          <Button
            variant="outlined"
            color="primary"
            onClick={onBack}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              py: 1.5,
            }}
          >
            Back
          </Button>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default DimensionalityReductionForm;
